import { ImpactHeadlineProps } from './types';
import { Headline } from './CustomHeadline';
import { cn, getColorway, getYMargins } from 'utils/styles';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { Colorways } from 'types/constants';
import Button from 'components/shared/Buttons/BaseButton';
import EmailCapture from 'components/shared/EmailCapture/EmailCapture';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { getEnrollmentUrl } from '@/utils/linkHelpers';

const ImpactHeadline = ({
  sys,
  sectionName,
  prebuiltHeadline,
  richTextHeadline,
  color,
  copy,
  ctas,
  margins,
  showEmailInput,
  emailInputButtonText,
  enrollmentUrl,
}: ImpactHeadlineProps) => {
  const [firstCta, secondCta] = ctas?.items ? ctas?.items : [null, null];
  const hasPrebuiltHeadline = !!prebuiltHeadline;
  const hasActionPrimaryColorway = color.name === Colorways.ActionPrimary;
  const italicColor = hasActionPrimaryColorway ? 'content-inverse' : 'text-accent-green-400';
  // fallback to the default enrollment URL if no enrollment URL is provided, it happens when the module is part of a Ninetailed experience
  const baseEnrollmentUrl = enrollmentUrl ?? getEnrollmentUrl();

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={`${getColorway(color)} ${getYMargins(margins)}`}>
      <div className="content-wrapper">
        <div className="flex flex-col gap-y-6 sm:gap-y-12">
          <Headline
            richTextHeadline={richTextHeadline}
            prebuiltHeadline={prebuiltHeadline}
            italicColor={italicColor}
            {...liveUpdateAttrs(sys?.id, 'prebuiltHeadline')}
          />
          <div
            className={cn('flex flex-col gap-y-6', {
              'min-[1440px]:relative min-[1440px]:left-2/4 min-[1440px]:w-1/2': hasPrebuiltHeadline,
            })}>
            {copy && (
              <div className="font-serif text-body-serif sm:max-w-[640px]" {...liveUpdateAttrs(sys?.id, 'copy')}>
                <RichTextCopy copy={copy} />
              </div>
            )}
            {showEmailInput && (
              <div className="flex flex-col gap-4">
                <EmailCapture
                  sys={sys}
                  buttonText={emailInputButtonText ?? 'Get Started'}
                  formClassName="md:flex-row lg:h-12 w-full md:w-md"
                  buttonClassName={`md:w-40 px-16 whitespace-nowrap`}
                  baseEnrollmentUrl={baseEnrollmentUrl}
                />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-y-4 gap-x-6" {...liveUpdateAttrs(sys?.id, 'cta')}>
              {firstCta && (
                <div className={'text-current md:max-w-[343px]'}>
                  <Button sys={firstCta.sys} href={firstCta.url} internalPage={firstCta?.internalPage}>
                    {firstCta.text}
                  </Button>
                </div>
              )}
              {secondCta && (
                <div className={'md:max-w-[343px]'}>
                  <Button
                    variant="outline"
                    ctaLevel="secondary"
                    sys={secondCta.sys}
                    href={secondCta.url}
                    internalPage={secondCta?.internalPage}
                    className="secondary-cta">
                    {secondCta.text}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactHeadline;
